import React from 'react';
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import TopBar from '../../components/topbar/TopBar';
import Footer from '../../components/footer/Footer';

// TODO(jessy): Add a small "What we are working on" sub-block under section 03 with one
// problem-shape line (e.g., "Hard problems in perception, motion planning, and the
// human-in-the-loop workflow software around both.") so hires have something present-tense
// to grab onto without revealing the product playbook.
// TODO(jessy): Once we have a blog post, demo video, or technical write-up, add a footer
// "Writing" or "Notes" link to surface artifacts beyond BlackStable for credibility.
// TODO(jessy): If/when committed investors or notable angels can be named, add a subtle
// "Backed by ___" line below the team list as a credibility multiplier for VCs and hires.
// TODO(jessy): Flesh out the footer with a small functional menu (Careers, BlackStable,
// founders' LinkedIn, Twitter/X) so the page ends with somewhere to land beyond the email.

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="grid-bg" aria-hidden="true" />
      <div className="hero-glow animate-pulse-slow" aria-hidden="true" />
      <div className="relative mx-auto max-w-6xl px-6 sm:px-8 pt-24 pb-16 sm:pt-32 sm:pb-24 z-10">
        <div className="flex flex-col items-start gap-8 max-w-4xl animate-fade-in-up">
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-gradient">
            Full-stack
            <br />
            autonomous machine care.
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground measure leading-relaxed font-light">
            Building AI and robotics to autonomously inspect, diagnose, and repair complex
            machines.
          </p>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <a
              href="#thesis"
              className="ghost-button inline-flex items-center gap-2 px-6 py-3 rounded-md"
            >
              Read Thesis
              <ArrowDown size={16} strokeWidth={1.8} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Thesis() {
  return (
    <section id="thesis" className="relative section-padding">
      <div className="mx-auto max-w-6xl px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-3">01 — Thesis</div>
            <h2 className="font-display text-3xl sm:text-4xl text-foreground leading-tight">
              Machines changed. Maintenance didn&rsquo;t.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-base sm:text-lg text-muted-foreground measure leading-relaxed">
            <p>
              Machines are becoming more intelligent, electrified, and software-defined. Yet the
              work required to keep them running remains fragmented, manual, and dependent on
              scarce expertise.
            </p>
            <p className="text-foreground">
              We believe the future of machine care is intelligent, embodied, and increasingly
              autonomous.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function BlackStable() {
  return (
    <section id="blackstable" className="relative section-padding">
      <div className="mx-auto max-w-6xl px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-3">02 — BlackStable</div>
            <h2 className="font-display text-3xl sm:text-4xl text-foreground leading-tight">
              Built for the real world.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-base sm:text-lg text-muted-foreground measure leading-relaxed">
            <p className="text-foreground">Autonomy must work beyond the lab.</p>
            <p>
              We own and operate{' '}
              <a
                href="https://blackstable.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group/bs inline-flex items-center gap-0.5 align-baseline text-foreground hover:text-primary transition-colors whitespace-nowrap"
              >
                BlackStable
                <ArrowUpRight
                  size={14}
                  strokeWidth={1.8}
                  className="text-muted-foreground/60 group-hover/bs:text-primary group-hover/bs:-translate-y-0.5 group-hover/bs:translate-x-0.5 transition-all"
                />
              </a>
              , a premium automotive service business in San Jose, California, and our first
              commercial deployment environment.
            </p>
            <p>Real machines. Real technicians. Real customers. Real-world complexity.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

const TEAM = [
  {
    name: 'Jessy Mwarage',
    role: 'Co-founder & CEO',
    bio: 'Previously Waymo, Rivian, Cruise, and X (Google X), working across systems architecture, vehicle diagnostics, embedded systems, and power systems. MIT SB, MIT SM.',
    linkedin: 'https://linkedin.com/in/jessymbagaramwarage',
  },
  {
    name: 'Rand Hidayah',
    role: 'Co-founder & Head of Robotics',
    bio: 'Founding contributor to Viam\u2019s Robotics Development Kit, spanning motion planning, motor control, and sensor systems. PhD research in exoskeleton robotics at Columbia; prior military vehicle engineering at KADDB. MIT SB, Columbia SM/PhD.',
    linkedin: 'https://linkedin.com/in/rhidayah',
  },
  {
    name: 'Bruce Cameron',
    role: 'Technology Adviser',
    bio: 'Senior Lecturer in Technology Strategy at MIT. Systems architecture and product platform expertise spanning NASA\u2019s Constellation Program and advisory work with Amazon, Hyundai, BP, and Nokia. University of Toronto BASc, MIT SM, MIT PhD.',
    linkedin: 'https://linkedin.com/in/brucegcameron',
  },
];

function TeamRow({ person }) {
  return (
    <li className="group relative">
      <a
        href={person.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between gap-4 py-5 transition-colors"
      >
        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
          <span className="font-display text-lg sm:text-xl text-foreground group-hover:text-primary transition-colors">
            {person.name}
          </span>
          <span className="font-mono text-[0.7rem] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground/70">
            {person.role}
          </span>
        </div>
        <ArrowUpRight
          size={18}
          strokeWidth={1.6}
          className="shrink-0 text-muted-foreground/50 group-hover:text-primary transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        />
      </a>
      <div className="pointer-events-none absolute left-0 right-0 sm:right-auto sm:max-w-md bottom-full mb-3 z-20 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-all duration-200">
        <div className="rounded-md border border-border bg-card/95 backdrop-blur-md px-5 py-4 shadow-2xl shadow-primary/10 relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute -top-1/2 -right-1/4 w-64 h-64 rounded-full bg-primary/10 blur-3xl pointer-events-none"
          />
          <p className="relative text-sm text-muted-foreground leading-relaxed">
            {person.bio}
          </p>
        </div>
      </div>
    </li>
  );
}

function Team() {
  return (
    <section id="team" className="relative section-padding">
      <div className="mx-auto max-w-6xl px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-3">03 — Who We Are</div>
            <h2 className="font-display text-3xl sm:text-4xl text-foreground leading-tight">
              Built by people who ship real systems.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-base sm:text-lg text-muted-foreground measure leading-relaxed">
            <p>
              Our team brings experience across autonomous vehicles, robotics, electrification,
              and complex systems engineering &mdash; from research through real-world deployment.
            </p>
            <ul className="pt-2 divide-y divide-border/60 border-y border-border/60">
              {TEAM.map((person) => (
                <TeamRow key={person.name} person={person} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function LandingPage({ theme, themePreference, onSetThemePreference }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <TopBar
        theme={theme}
        themePreference={themePreference}
        onSetThemePreference={onSetThemePreference}
      />
      <main className="flex-1">
        <Hero />
        <Thesis />
        <BlackStable />
        <Team />
      </main>
      <Footer />
    </div>
  );
}

export default LandingPage;
